import React from 'react';
import { FiAlertTriangle, FiTrendingUp, FiUsers, FiCheckCircle, FiClock,  FiShield, FiBarChart2 } from 'react-icons/fi';

const EarlyWarningSystem = () => {
  const warningIndicators = [
    {
      icon: <FiAlertTriangle className="w-6 h-6" />,
      title: 'Attendance Drops Below 75%',
      description: 'When a student\'s attendance falls below 75%, it\'s one of the strongest indicators of academic risk. Students who miss classes miss content, fall behind, and are less likely to succeed. Early identification allows timely intervention.',
      stat: '80% of students with attendance below 75% struggle academically',
      solution: 'Truyon tracks attendance patterns in real-time and alerts advisors when students cross the threshold.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Declining Academic Performance',
      description: 'A drop in grades across multiple courses or a significant decline in a single course signals trouble. Students who start strong then decline often face challenges that can be addressed with support.',
      stat: '70% of students with grade drops of 20% or more fail to recover without intervention',
      solution: 'Truyon monitors grade patterns and flags students showing concerning trends.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Late Submissions and Missed Deadlines',
      description: 'Students who consistently submit assignments late or miss deadlines are struggling with time management, course load, or personal issues. These behaviors are early warning signs of academic difficulty.',
      stat: '60% of students with chronic late submissions are at risk of failing courses',
      solution: 'Truyon tracks submission patterns and alerts advisors to concerning behaviors.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Reduced Course Engagement',
      description: 'Students who stop participating in class discussions, don\'t join study groups, or avoid interacting with faculty are disengaging. This disengagement often precedes academic failure.',
      stat: '75% of disengaged students show declining performance within 6 weeks',
      solution: 'Truyon tracks engagement metrics and flags students showing reduced participation.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Financial or Personal Concerns',
      description: 'Students facing financial difficulties, health issues, or family problems often show academic signs first. Changes in attendance, performance, or engagement may signal underlying challenges.',
      stat: '50% of student withdrawals are due to financial or personal issues',
      solution: 'Truyon helps advisors track multiple indicators and identify students who may need support.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Gaps Between Potential and Performance',
      description: 'Students with strong backgrounds but poor performance may be struggling to adapt. These gaps often respond well to targeted academic support and mentoring.',
      stat: '65% of students with performance gaps improve with early intervention',
      solution: 'Truyon compares student potential with actual performance to identify gaps early.',
      color: 'bg-blue-50'
    }
  ];

  const interventionSteps = [
    {
      title: 'Identify',
      description: 'The system identifies at-risk students based on multiple indicators—attendance, grades, engagement, and more.',
      icon: <FiAlertTriangle className="w-5 h-5" />
    },
    {
      title: 'Alert',
      description: 'Advisors, faculty, and support staff receive automated alerts about students who need attention.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Intervene',
      description: 'Targeted interventions are deployed—academic support, counseling, financial aid, or mentoring.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Monitor',
      description: 'Student progress is tracked continuously. Adjust interventions based on response.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Support',
      description: 'Students receive ongoing support until they\'re back on track. Early intervention prevents failure.',
      icon: <FiCheckCircle className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Every year, thousands of students in India drop out of higher education. Some leave because of financial difficulties. Others struggle academically. Many simply disengage, stop attending classes, and disappear. The tragedy is that most of these students could have been helped—if their institutions had known they were struggling.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">Early warning systems are changing this.</strong> By analyzing student data—attendance, grades, engagement, and other indicators—these systems identify at-risk students before they fail. Faculty and advisors receive alerts, intervene early, and help students get back on track.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how early warning systems work in higher education—and why every college and university needs one.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">80%</div>
          <div className="text-sm text-slate-500">At-risk students identified by attendance drops</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">65%</div>
          <div className="text-sm text-slate-500">Improve with early intervention</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">30%</div>
          <div className="text-sm text-slate-500">Reduction in student dropout rates</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Early Warning Indicators: What the System Looks For</h2>

      {/* Warning Indicators */}
      {warningIndicators.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">{item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Intervention Process */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Early Warning Intervention Process</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {interventionSteps.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Reactive vs. Proactive Student Support</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Reactive Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Wait for students to fail</span></li>
              <li className="flex items-start gap-2">• <span>No visibility into student struggles</span></li>
              <li className="flex items-start gap-2">• <span>Intervention comes too late</span></li>
              <li className="flex items-start gap-2">• <span>Higher dropout rates</span></li>
              <li className="flex items-start gap-2">• <span>Frustrated students and faculty</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Proactive Early Warning System</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Identify at-risk students early</span></li>
              <li className="flex items-start gap-2">• <span>Real-time visibility into student data</span></li>
              <li className="flex items-start gap-2">• <span>Intervention before failure occurs</span></li>
              <li className="flex items-start gap-2">• <span>Lower dropout rates</span></li>
              <li className="flex items-start gap-2">• <span>Supported, successful students</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Powers Effective Early Warning Systems</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔍</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Monitoring</h4>
            <p className="text-xs text-slate-600">Track student data continuously</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Multi-Indicator Analysis</h4>
            <p className="text-xs text-slate-600">Attendance, grades, engagement, and more</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🚨</div>
            <h4 className="font-bold text-slate-900 text-sm">Automated Alerts</h4>
            <p className="text-xs text-slate-600">Notify advisors and faculty instantly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Targeted Interventions</h4>
            <p className="text-xs text-slate-600">Deploy the right support for each student</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Progress Tracking</h4>
            <p className="text-xs text-slate-600">Monitor intervention effectiveness</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🤝</div>
            <h4 className="font-bold text-slate-900 text-sm">Collaborative Support</h4>
            <p className="text-xs text-slate-600">Advisors, faculty, and support staff work together</p>
          </div>
        </div>
      </div>

      {/* Success Story */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Impact of Early Intervention</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <p className="text-sm text-slate-600 italic">"A student was flagged for poor attendance and dropping grades in week 4. The advisor reached out, learned about a family emergency, and connected the student with academic support. The student caught up and graduated on time. Without the early warning system, they would have dropped out."</p>
            <p className="text-xs font-semibold text-emerald-700 mt-2">— Dean of Students, Private University</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <p className="text-sm text-slate-600 italic">"We used to lose 15% of our first-year students. After implementing an early warning system, we reduced that to 8%. The biggest impact came from identifying struggling students and providing support in their first semester."</p>
            <p className="text-xs font-semibold text-emerald-700 mt-2">— Principal, Degree College</p>
          </div>
        </div>
      </div>

      {/* The Human Impact */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Human Impact of Early Warning Systems</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Behind every student who drops out is a story. A dream interrupted. A family disappointed. A future changed. Early warning systems help institutions write different stories—stories of support, resilience, and success.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          When we identify at-risk students early, we give them a second chance. We send a message that their institution cares. We help them overcome challenges and achieve their goals. It's not just about retention—it's about believing in our students.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Early warning systems don't just save students. They change lives.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Intervene Before It's Too Late with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Every student who drops out is a loss—to the student, to the institution, and to society. Early warning systems help prevent these losses by identifying at-risk students early and enabling timely intervention.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon's early warning system monitors attendance, grades, engagement, and other indicators in real-time. Advisors and faculty receive automated alerts and can intervene before students fail.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for early warning systems.</strong> Identify risk early. Intervene effectively. Support students to success.
        </p>
      </div>
    </>
  );
};

export default EarlyWarningSystem;